import { Controller } from "@hotwired/stimulus"

const PRESETS = [ 15, 30, 45, 60 ]

// Connects to data-controller="break" — ports the mockup's break UI from
// RegistrationForm.tsx: quick-tap preset chips (tapping the active one again
// clears it), a dashed "Other…" button that reveals a custom minutes field,
// a "No break" clear link once a break is set, and a live "= X hours worked"
// preview computed from every time block's start/end plus the break,
// recomputed on every change (including blocks added/removed by
// time-blocks#add/#remove — see data-action on this controller's root
// element). break_minutes itself is a single real field (see
// #customInputTarget) — presets just write into it; nothing extra is
// submitted.
export default class extends Controller {
  static targets = [ "startTime", "endTime", "preset", "clearButton", "customWrapper", "customInput", "otherButton", "error", "preview" ]
  // Every piece of text and the number formatting come in from the view, so
  // this controller holds no language of its own. `worked` and `breakNote`
  // are templates carrying %{...} placeholders straight from the locale file.
  static values = {
    tooLong: String,
    overlap: String,
    worked: String,
    breakNote: String,
    hoursUnit: String,
    minutesUnit: String,
    locale: String
  }

  connect() {
    const minutes = this.currentMinutes()
    this.customOpen = minutes > 0 && !PRESETS.includes(minutes)
    this.render()
  }

  selectPreset(event) {
    const minutes = event.params.minutes
    const current = this.currentMinutes()
    this.customOpen = false
    this.setMinutes(current === minutes ? 0 : minutes)
  }

  openCustom() {
    this.customOpen = true
    this.render()
    this.customInputTarget.focus()
  }

  clear() {
    this.customOpen = false
    this.setMinutes(0)
  }

  recompute() {
    this.render()
  }

  setMinutes(minutes) {
    this.customInputTarget.value = minutes > 0 ? minutes : ""
    this.render()
  }

  currentMinutes() {
    const value = Number(this.customInputTarget.value)
    return Number.isFinite(value) && value > 0 ? value : 0
  }

  render() {
    const minutes = this.currentMinutes()

    this.customWrapperTarget.hidden = !this.customOpen
    this.otherButtonTarget.hidden = this.customOpen
    this.clearButtonTarget.hidden = minutes === 0

    for (const button of this.presetTargets) {
      const active = !this.customOpen && Number(button.dataset.breakMinutesParam) === minutes
      button.classList.toggle("border-brand-600", active)
      button.classList.toggle("bg-brand-600", active)
      button.classList.toggle("text-white", active)
      button.classList.toggle("font-semibold", active)
      button.classList.toggle("border-stone-300", !active)
      button.classList.toggle("bg-white", !active)
      button.classList.toggle("text-stone-700", !active)
      button.classList.toggle("font-medium", !active)
      // Only the inactive look gets a hover state — an already-selected chip
      // shouldn't visually shift on hover.
      button.classList.toggle("hover:border-brand-400", !active)
      button.classList.toggle("hover:text-brand-700", !active)
    }

    this.updatePreview(minutes)
  }

  grossMinutes() {
    return this.filledSpans().reduce((total, span) => total + span.minutes, 0)
  }

  // Same rule the model enforces: overlapping blocks would bill the same clock
  // time twice. Touching blocks (16:00 ends, 16:00 starts) are a split day.
  hasOverlap() {
    const spans = this.filledSpans().sort((a, b) => a.start - b.start)

    return spans.some((span, index) => index > 0 && span.start < spans[index - 1].end)
  }

  // Every visible (not-removed) block as a start/end/duration triple, pairing
  // the i-th startTime with the i-th endTime — safe because removing a block
  // always removes its whole fieldset, so the two target arrays stay in step.
  filledSpans() {
    const spans = []

    this.startTimeTargets.forEach((startInput, index) => {
      if (startInput.closest("[data-time-blocks-target='block']")?.hidden) return

      const endInput = this.endTimeTargets[index]
      if (!startInput.value || !endInput || !endInput.value) return

      const minutes = this.timeDiffMinutes(startInput.value, endInput.value)
      if (minutes <= 0) return

      spans.push({ start: this.minutesOfDay(startInput.value), end: this.minutesOfDay(endInput.value), minutes: minutes })
    })

    return spans
  }

  minutesOfDay(value) {
    const [ hours, minutes ] = value.split(":").map(Number)
    return (hours * 60) + minutes
  }

  timeDiffMinutes(start, end) {
    const [ startHours, startMinutes ] = start.split(":").map(Number)
    const [ endHours, endMinutes ] = end.split(":").map(Number)
    return (endHours * 60 + endMinutes) - (startHours * 60 + startMinutes)
  }

  updatePreview(breakMinutes) {
    const grossMinutes = this.grossMinutes()

    if (grossMinutes <= 0) {
      this.previewTarget.hidden = true
      this.errorTarget.hidden = true
      return
    }

    if (this.hasOverlap()) {
      this.previewTarget.hidden = true
      this.errorTarget.textContent = this.overlapValue
      this.errorTarget.hidden = false
      return
    }

    if (breakMinutes >= grossMinutes) {
      this.previewTarget.hidden = true
      this.errorTarget.textContent = this.tooLongValue
      this.errorTarget.hidden = false
      return
    }

    this.errorTarget.hidden = true

    const workedHours = Math.round(((grossMinutes - breakMinutes) / 60) * 100) / 100
    const workedLabel = `<span class="font-medium text-stone-900">${workedHours.toLocaleString(this.localeValue)}</span>`
    let html = this.workedValue.replace("%{hours}", workedLabel)
    if (breakMinutes > 0) {
      const note = this.breakNoteValue
        .replace("%{gross}", this.formatMinutes(grossMinutes))
        .replace("%{break}", this.formatMinutes(breakMinutes))
      html += ` <span class="text-stone-400">${note}</span>`
    }
    this.previewTarget.innerHTML = html
    this.previewTarget.hidden = false
  }

  formatMinutes(minutes) {
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    if (hours === 0) return `${mins} ${this.minutesUnitValue}`
    return mins === 0 ? `${hours} ${this.hoursUnitValue}` : `${hours} ${this.hoursUnitValue} ${mins} ${this.minutesUnitValue}`
  }
}
